import React, { createContext, useState, useContext, useEffect } from "react";

// Define shape of customer data
const defaultCustomer = {
  _id: null,
  name: "",
  email: "",
  phone: "",
  address: "",
  services: [],
  token: "",
  isVerified: false,
};

const CustomerContext = createContext({
  customer: defaultCustomer,
  isLoading: true,
  isAuthenticated: false,
  loginCustomer: () => {},
  logoutCustomer: () => {},
  updateCustomer: () => {},
});

export const CustomerProvider = ({ children }) => {
  const [customer, setCustomer] = useState(defaultCustomer);
  const [isLoading, setIsLoading] = useState(true);

  // Load customer from localStorage on initial render
  useEffect(() => {
    const loadCustomer = async () => {
      try {
        const storedToken = localStorage.getItem("customerToken");
        const storedCustomer = localStorage.getItem("customerData");

        if (storedToken && storedCustomer) {
          const parsedCustomer = JSON.parse(storedCustomer);

          const customerWithToken = {
            ...parsedCustomer,
            token: storedToken,
            isVerified: true,
          };

          setCustomer(customerWithToken);
        }
      } catch (error) {
        console.error("Failed to parse customer data", error);
      } finally {
        setIsLoading(false);
      }
    };

    loadCustomer();
  }, []);

  const loginCustomer = (customerData, token) => {
    const completeCustomer = {
      ...defaultCustomer,
      ...customerData,
      token,
      isVerified: true,
    };

    setCustomer(completeCustomer);
    localStorage.setItem("customerData", JSON.stringify(completeCustomer));
    localStorage.setItem("customerToken", token);
  };

  const logoutCustomer = () => {
    setCustomer(defaultCustomer);
    localStorage.removeItem("customerData");
    localStorage.removeItem("customerToken");
  };

  const updateCustomer = (updatedData) => {
    const updatedCustomer = {
      ...customer,
      ...updatedData,
    };

    setCustomer(updatedCustomer);
    localStorage.setItem("customerData", JSON.stringify(updatedCustomer));
  };

  const value = {
    customer,
    isLoading,
    isAuthenticated: !!customer?._id,
    loginCustomer,
    logoutCustomer,
    updateCustomer,
  };

  return (
    <CustomerContext.Provider value={value}>
      {children}
    </CustomerContext.Provider>
  );
};

export const useCustomer = () => {
  const context = useContext(CustomerContext);
  if (!context) {
    throw new Error("useCustomer must be used within a CustomerProvider");
  }
  return context;
};
